import { isSodimacStandalone } from '@digital-retail/store-manager';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getVariant } from '../../../utils/variant';

/**
 * Custom hook that encapsulates all business logic for ProductTopSpecifications
 * @param {Object} props - Component props
 * @returns {Object} - Processed data and handlers
 */
export const useProductTopSpecifications = ({ product, labels, scollToElementId, digitalData, appCtx }) => {
  const [isDrawerActive, setIsDrawerActive] = useState(false);

  const processedData = useMemo(() => {
    // Destructure product attributes with safe navigation
    const {
      attributes: { topSpecifications = [], specifications = [] } = {},
      variants,
      currentVariant,
    } = product ?? {};

    // Destructure appCtx with safe navigation
    const { deviceType, store } = appCtx ?? {};
    const isSodimacSA = isSodimacStandalone(store);

    // Destructure labels with safe navigation and fallbacks
    const {
      SO_COM_TOP_SPECIFICATIONS = 'Características principales',
      SO_COM_VIEW_MORE_SPECIFICATONS = 'Ver más',
      SO_COMSPECIFICATIONS_LABEL = 'Ver más',
      TOP_SPECIFICATIONS,
      VIEW_MORE_SPECIFICATONS,
      SPECIFICATIONS_LABEL,
    } = labels ?? {};

    // Use destructured values directly with conditional assignment
    const topSpecificationsTitle = isSodimacSA ? SO_COM_TOP_SPECIFICATIONS : TOP_SPECIFICATIONS;
    const viewMoreText = isSodimacSA ? SO_COM_VIEW_MORE_SPECIFICATONS : VIEW_MORE_SPECIFICATONS;
    const specificationsTitle = isSodimacSA ? SO_COMSPECIFICATIONS_LABEL : SPECIFICATIONS_LABEL;

    const comboData = getVariant(variants, currentVariant)?.comboData ?? null;

    return {
      topSpecifications,
      specifications,
      deviceType,
      store,
      isSodimacSA,
      topSpecificationsTitle,
      viewMoreText,
      specificationsTitle,
      comboData,
      variants,
      currentVariant,
    };
  }, [product, labels, appCtx]);

  const activateDrawer = useCallback((isActive) => {
    setIsDrawerActive(isActive);
  }, []);

  const handleViewMoreClick = useCallback(() => {
    activateDrawer(true);
  }, [activateDrawer]);

  useEffect(() => {
    // Use safe navigation for digitalData access
    const { pdp: { setProductFeatures } = {} } = digitalData ?? {};

    if (setProductFeatures) {
      const hasTopSpecs = processedData.topSpecifications.length > 0;
      setProductFeatures(hasTopSpecs ? '2' : '');
    }
  }, [digitalData, processedData.topSpecifications.length]);

  // Computed values using modern syntax
  const computedValues = useMemo(
    () => ({
      shouldRender: processedData.topSpecifications.length > 0,
      isDesktop: processedData.deviceType === 'desktop',
      isMobile: processedData.deviceType !== 'desktop',
      hasComboSpecifications: Boolean(processedData.comboData?.hasSpecifications),
      shouldShowDivider: processedData.deviceType === 'desktop',
    }),
    [processedData.topSpecifications.length, processedData.deviceType, processedData.comboData?.hasSpecifications]
  );

  // Drawer toggle state for external access
  const drawerToggleState = useMemo(() => [isDrawerActive, activateDrawer], [isDrawerActive, activateDrawer]);

  return {
    // Processed data with spread
    ...processedData,

    // State values
    isDrawerActive,
    drawerToggleState,

    // Event handlers
    activateDrawer,
    handleViewMoreClick,

    // Computed values with spread
    ...computedValues,

    // Props for external components
    scollToElementId,
  };
};
